/**
 * Screen capture helpers built on getDisplayMedia + MediaRecorder.
 *
 * The browser shows a system-level picker so the user chooses what to share
 * (whole screen / window / browser tab). We just receive the stream, record
 * it to webm/mp4, and hand the user a download.
 *
 * Why not "just grab the canvas"? The editor renders with HTML/SVG/<video>,
 * not a single <canvas>, so canvas.captureStream() doesn't apply. Capturing
 * the surrounding browser tab gives a pixel-perfect result without us having
 * to rasterise anything.
 */

const MIME_CANDIDATES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4',
];

function pickMime(): string {
  for (const m of MIME_CANDIDATES) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

export interface RecordingResult {
  blob: Blob;
  ext: string;
  mime: string;
}

export class RecordingSession {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mime = '';
  private startedAt = 0;

  /** Returns true if the underlying recorder is still capturing. */
  get active(): boolean {
    return !!this.recorder && this.recorder.state === 'recording';
  }

  /** Elapsed ms since start. */
  get elapsedMs(): number {
    return this.startedAt ? Date.now() - this.startedAt : 0;
  }

  async start(
    opts: { fps?: number; audio?: boolean; cropElement?: Element | null } = {},
  ): Promise<{ cropped: boolean }> {
    if (this.recorder) throw new Error('Recording already in progress');
    // Self-capture + region crop is Chrome-only. We request the current tab
    // so the user doesn't have to pick anything; if that's rejected we
    // fall back to a normal getDisplayMedia call.
    const constraints: MediaStreamConstraints & {
      preferCurrentTab?: boolean;
      selfBrowserSurface?: 'include' | 'exclude';
    } = {
      video: { frameRate: opts.fps ?? 30 },
      audio: opts.audio ?? false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
    };
    try {
      this.stream = await navigator.mediaDevices.getDisplayMedia(constraints);
    } catch {
      // Older browsers / no-self-capture path — open the standard picker
      this.stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: opts.fps ?? 30 },
        audio: opts.audio ?? false,
      });
    }
    let cropped = false;
    if (opts.cropElement) {
      cropped = await this.tryCropTo(opts.cropElement);
    }
    // If the user stops sharing from the browser banner, we stop too.
    this.stream.getVideoTracks()[0]?.addEventListener('ended', () => {
      if (this.recorder?.state === 'recording') this.recorder.stop();
    });
    this.mime = pickMime();
    this.chunks = [];
    this.recorder = new MediaRecorder(
      this.stream,
      this.mime ? { mimeType: this.mime } : undefined,
    );
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.startedAt = Date.now();
    this.recorder.start(100);
    return { cropped };
  }

  private async tryCropTo(target: Element): Promise<boolean> {
    const w = window as unknown as {
      CropTarget?: { fromElement(el: Element): Promise<unknown> };
    };
    const track = this.stream?.getVideoTracks()[0] as MediaStreamTrack & {
      cropTo?: (t: unknown) => Promise<void>;
    };
    if (!w.CropTarget || !track || typeof track.cropTo !== 'function') return false;
    try {
      const ct = await w.CropTarget.fromElement(target);
      await track.cropTo(ct);
      return true;
    } catch (err) {
      console.warn('Region capture cropTo failed', err);
      return false;
    }
  }

  async stop(): Promise<RecordingResult | null> {
    if (!this.recorder) return null;
    const recorder = this.recorder;
    const stream = this.stream;
    const mime = this.mime || 'video/webm';
    const finished = new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(this.chunks, { type: mime }));
    });
    if (recorder.state !== 'inactive') recorder.stop();
    stream?.getTracks().forEach((t) => t.stop());
    this.recorder = null;
    this.stream = null;
    this.startedAt = 0;
    const blob = await finished;
    const ext = mime.includes('mp4') ? 'mp4' : 'webm';
    return { blob, ext, mime };
  }
}

export async function captureScreenshot(): Promise<Blob | null> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: false,
  });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play().catch(() => undefined);
    // Wait one frame so the first painted pixels are ready
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    await new Promise((r) => setTimeout(r, 80));
    const w = video.videoWidth || 1920;
    const h = video.videoHeight || 1080;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png'),
    );
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}
